/**
 * PATH: packages/bills/src/components/billsQueueModel.ts
 * FILE_ID: BILLS-PKG-CMP-QUEUE-MODEL-001
 * PURPOSE: Pure domain model for the bills queue — the filter rail
 *          (All / Extracted / Manual review / Couldn't fetch), amount
 *          provenance, blocked-reason copy, and bulk-action eligibility.
 *
 * WHY: Everything here is pure data-in → data-out so the invariants are
 *      unit-testable without rendering:
 *        - every rail chip maps to a real server status filter,
 *        - bulk actions can never silently include human-touched rows.
 *
 * DEPENDENCIES: ../types only. No React.
 */

import {
  BLOCKED_STATUSES,
  isFailClosedStatus,
  type BillFlag,
  type BillListRow,
  type BillsListCounts,
  type BillsQueueSummary,
  type BillStatus,
  type BillStatusFilter,
} from '../types';

/* ────────────────────────────────────────────────────────────────────
 * Queue filter — one status axis + one optional attention axis.
 * ──────────────────────────────────────────────────────────────────── */

export interface QueueFilter {
  status: BillStatusFilter;
  /** Attention-reason refinement. Mutually exclusive with a non-'all'
   * status in the UI (picking a reason resets status to 'all' and vice
   * versa) so the operator always knows which single filter is live. */
  flag?: BillFlag;
}

/* ────────────────────────────────────────────────────────────────────
 * The filter rail — All + three working queues. A deliberate SUBSET of
 * the pipeline (not a partition): the rail shows only the queues an
 * operator actually works; everything else is reachable via search /
 * deep links, not chips.
 * ──────────────────────────────────────────────────────────────────── */

export interface PipelineChip {
  key: BillStatusFilter;
  label: string;
  count: number;
  title: string;
}

export interface PipelinePartition {
  chips: PipelineChip[];
  total: number;
}

/**
 * The three working queues, in triage order: Extracted (machine read it,
 * a person reviews), Manual review (the verifier wants a second look),
 * Couldn't fetch (document never downloaded). Each maps 1:1 to a server
 * status filter.
 */
export function buildPipelinePartition(
  counts: BillsListCounts | undefined,
): PipelinePartition {
  const by = counts?.by_status ?? {};
  const total = counts?.total ?? 0;
  const chips: PipelineChip[] = [
    {
      key: 'extracted',
      label: 'Extracted',
      count: by.extracted ?? 0,
      title: 'Read by the machine — a person needs to check and approve.',
    },
    {
      key: 'needs_review',
      label: 'Manual review',
      count: by.needs_review ?? 0,
      title:
        'The verifier could not decide whether this is a genuine bill — ' +
        'a person takes a second look and restores or dismisses it.',
    },
    {
      key: 'needs_fetch',
      label: "Couldn't fetch",
      count: by.needs_fetch ?? 0,
      title:
        'The attachment could not be downloaded. Retried automatically in ' +
        'the background — step in only if the retries never recover it.',
    },
  ];
  return { chips, total };
}

/* ────────────────────────────────────────────────────────────────────
 * Amount provenance — a total keyed by a human must not look identical
 * to one guessed at 0.1 confidence (design premise #4).
 * ──────────────────────────────────────────────────────────────────── */

export type AmountProvenance =
  | { kind: 'human'; label: string }
  | { kind: 'ai'; confidence: number; low: boolean; label: string }
  | { kind: 'unknown'; label: string };

const LOW_CONFIDENCE = 0.8;

export function amountProvenance(row: BillListRow): AmountProvenance {
  if (row.total == null || row.total === '') {
    return { kind: 'unknown', label: 'No amount read from the document' };
  }
  if (row.source === 'manual') {
    return { kind: 'human', label: 'Entered by a person' };
  }
  if ((row.corrections_count ?? 0) > 0) {
    return {
      kind: 'human',
      label: row.reviewed_by_name
        ? `Corrected by ${row.reviewed_by_name}`
        : 'Corrected by a person',
    };
  }
  if (row.status === 'reviewed' || row.status === 'pushed') {
    return {
      kind: 'human',
      label: row.reviewed_by_name
        ? `Approved by ${row.reviewed_by_name}`
        : 'Approved by a person',
    };
  }
  const conf = row.extraction_confidence == null
    ? null
    : Number(row.extraction_confidence);
  const confidence = conf != null && Number.isFinite(conf) ? conf : 0;
  const low = confidence < LOW_CONFIDENCE;
  return {
    kind: 'ai',
    confidence,
    low,
    label: low
      ? `Machine-read at low confidence (${Math.round(confidence * 100)}%) — check against the document`
      : `Machine-read (${Math.round(confidence * 100)}% confidence) — not yet checked by a person`,
  };
}

/* ────────────────────────────────────────────────────────────────────
 * Blocked-reason copy — why is this row stuck, in operator language,
 * derivable from list-row data alone (no detail fetch).
 * ──────────────────────────────────────────────────────────────────── */

const VERIFICATION_REASON_COPY: Record<string, string> = {
  no_total: 'No total could be verified against the document',
  totals_mismatch: "Line items don't add up to the total",
  self_issued: 'Looks like our own outgoing invoice, not a vendor bill',
  verifier_rejected: 'Verification decided this is not a genuine bill',
  low_confidence: 'Read at too low a confidence to trust',
  duplicate: 'Suspected duplicate of another bill',
};

export function blockedReason(row: BillListRow): string | null {
  if (row.status === 'failed') {
    if (row.reviewed_at) return 'Rejected by a person';
    return row.extraction_error?.trim()
      ? `Couldn't read the invoice — ${row.extraction_error.trim()}`
      : "Couldn't read the invoice";
  }
  if (!isFailClosedStatus(row.status)) return null;
  if (row.status === 'awaiting_document') {
    return 'No usable document arrived with the email';
  }
  if (row.status === 'needs_fetch') {
    const n = row.auto_retry_count ?? 0;
    return n > 0
      ? `Couldn't download the attachment — retried automatically ${n}\u00d7`
      : "Couldn't download the attachment";
  }
  const reason = row.verification_reason?.trim();
  if (reason && VERIFICATION_REASON_COPY[reason]) {
    return VERIFICATION_REASON_COPY[reason];
  }
  if (row.status === 'quarantined') return 'Held back by a verification check';
  return 'Needs a second look — the verifier could not decide';
}

/* ────────────────────────────────────────────────────────────────────
 * Bulk actions — every action previews exactly what it will touch and
 * what it will skip. Retry-read NEVER includes human-touched rows.
 * ──────────────────────────────────────────────────────────────────── */

export type BulkAction = 'retry' | 'restore' | 'dismiss';

export interface BulkPlan {
  action: BulkAction;
  /** Rows the action will run on. */
  eligible: BillListRow[];
  /** Rows excluded, each with the reason shown in the preview. */
  skipped: Array<{ row: BillListRow; reason: string }>;
}

export function humanTouched(row: BillListRow): boolean {
  return (
    row.source === 'manual' ||
    (row.corrections_count ?? 0) > 0 ||
    row.status === 'reviewed' ||
    row.status === 'pushed' ||
    !!row.reviewed_at
  );
}

export function planBulkAction(
  action: BulkAction,
  rows: BillListRow[],
): BulkPlan {
  const eligible: BillListRow[] = [];
  const skipped: BulkPlan['skipped'] = [];
  for (const row of rows) {
    if (action === 'retry') {
      if (!row.email_id) {
        skipped.push({ row, reason: 'No source email to re-read' });
      } else if (humanTouched(row)) {
        // Hard guard, no override in bulk: re-reading would overwrite a
        // person's work (the backend also refuses with 409 — this keeps
        // the preview honest instead of relying on the server to bounce).
        skipped.push({ row, reason: 'Touched by a person — re-reading would overwrite their work' });
      } else {
        eligible.push(row);
      }
    } else if (action === 'restore') {
      if (isFailClosedStatus(row.status)) {
        eligible.push(row);
      } else {
        skipped.push({ row, reason: 'Not held back — nothing to restore' });
      }
    } else {
      // dismiss (reject) — anything not already in Xero or already rejected.
      if (row.status === 'pushed') {
        skipped.push({ row, reason: 'Already in Xero — cannot dismiss' });
      } else if (row.status === 'failed' && row.reviewed_at) {
        skipped.push({ row, reason: 'Already rejected' });
      } else {
        eligible.push(row);
      }
    }
  }
  return { action, eligible, skipped };
}

export const BULK_ACTION_COPY: Record<
  BulkAction,
  { label: string; verb: string; detail: string }
> = {
  retry: {
    label: 'Retry read',
    verb: 'Re-read',
    detail:
      'Runs the machine read again on each source email. Rows a person has approved or corrected are skipped — their work is never overwritten.',
  },
  restore: {
    label: 'Restore',
    verb: 'Restore',
    detail:
      'Returns held-back bills to the queue they were in before verification held them.',
  },
  dismiss: {
    label: 'Dismiss',
    verb: 'Dismiss',
    detail:
      'Marks each bill as rejected (not a payable bill). Kept for the audit trail — nothing is deleted.',
  },
};

/* ────────────────────────────────────────────────────────────────────
 * Money header — the known / unknown / overdue line. Server-computed
 * over the FULL filtered set; this only shapes it for display.
 * ──────────────────────────────────────────────────────────────────── */

export interface QueueMoneyLine {
  /** One entry per currency, largest population first (server order). */
  currencies: Array<{
    currency: string;
    knownTotal: number;
    knownCount: number;
    overdueTotal: number;
    overdueCount: number;
  }>;
  /** Bills with NO trustworthy amount — a first-class figure. */
  unknownCount: number;
  knownCount: number;
  overdueCount: number;
}

export function buildMoneyLine(
  summary: BillsQueueSummary | undefined,
): QueueMoneyLine | null {
  if (!summary) return null;
  return {
    currencies: summary.by_currency.map((r) => ({
      currency: r.currency || 'AUD',
      knownTotal: Number(r.known_total) || 0,
      knownCount: r.known_count,
      overdueTotal: Number(r.overdue_known_total) || 0,
      overdueCount: r.overdue_count,
    })),
    unknownCount: summary.unknown_count,
    knownCount: summary.known_count,
    overdueCount: summary.overdue_count,
  };
}

/** Statuses whose rows should surface the blockedReason strip. */
export function isBlockedStatus(status: BillStatus): boolean {
  return (BLOCKED_STATUSES as readonly string[]).includes(status);
}
